// German for the Agent page: the instrument line, the dispatch deck and scope picker, the pass
// in flight, the run ledger and its expansion, the corpus dock, the delete modal. Registered on load.
import { registerDe } from '../i18n/index.js';

registerDe({
  // ── the instrument line ──
  'The execution plane is down.': 'Die Ausführungsebene ist ausgefallen.',
  'vLLM at': 'vLLM unter',
  'did not answer.': 'hat nicht geantwortet.',
  'The endpoint said: “{error}”.': 'Der Endpunkt meldete: «{error}».',
  'Nothing can be dispatched — every pass below would die at its first turn. The register stays fully usable by hand.':
    'Nichts kann gestartet werden — jeder Durchgang unten würde in seiner ersten Runde sterben. Das Register bleibt von Hand voll nutzbar.',
  'Re-check': 'Erneut prüfen',
  'Ready': 'Bereit',
  'Checking…': 'Wird geprüft…',
  'no model reported': 'kein Modell gemeldet',
  'workspace': 'Workspace',
  'docs at': 'Dokumente bei',
  'built automatically when the first run starts': 'wird automatisch gebaut, wenn der erste Lauf startet',
  'of': 'von',
  'slots in use': 'Slots belegt',
  'this run stops after': 'dieser Lauf endet nach',
  'min': 'Min.',
  'or': 'oder',
  'turns': 'Runden',
  'both run slots free': 'beide Lauf-Slots frei',
  '{n} run slots free': '{n} Lauf-Slots frei',
  'Rebuilding…': 'Wird neu gebaut…',
  'Rebuild workspace': 'Workspace neu bauen',

  // ── the deck ──
  'vLLM is unreachable — the run would die at its first turn.': 'vLLM ist nicht erreichbar — der Lauf würde in seiner ersten Runde sterben.',
  'Two runs are already live.': 'Zwei Läufe sind bereits aktiv.',
  'since': 'seit',
  'linked': 'verknüpft',
  'uncovered': 'unabgedeckt',
  'armed': 'scharf',
  '+{n} more': '+{n} weitere',
  'no subject': 'kein Gegenstand',
  'Operator note for this pass — it travels into the prompt': 'Operator-Notiz für diesen Durchgang — sie wandert in den Prompt',
  'Dispatch': 'Starten',

  // ── the scope picker ──
  'Choose documents': 'Dokumente wählen',
  'selected': 'ausgewählt',
  'All': 'Alle',
  'Not yet covered ({n})': 'Noch nicht abgedeckt ({n})',
  'None': 'Keine',
  'Close': 'Schliessen',
  'No document is linked to this engagement, so a batch pass would have nothing to read.':
    'Kein Dokument ist mit diesem Auftrag verknüpft, ein Batch-Durchgang hätte also nichts zu lesen.',
  'no text layer': 'keine Textebene',
  'covered': 'abgedeckt',
  'not yet covered': 'noch nicht abgedeckt',
  'Optional instruction — with one, this runs as a targeted pass instead of a batch pass':
    'Optionale Anweisung — mit einer läuft dies als gezielter Durchgang statt als Batch-Durchgang',
  'Starting…': 'Wird gestartet…',
  'Start targeted run': 'Gezielten Lauf starten',
  'Start batch run ({n} docs)': 'Batch-Lauf starten ({n} Dok.)',

  // ── the pass in flight ──
  'running': 'läuft',
  'tool calls': 'Tool-Aufrufe',
  'Stop': 'Stopp',
  'documents in scope accounted for.': 'Dokumenten im Umfang erfasst.',
  'A run ends only when': 'Ein Lauf endet erst, wenn',
  'accepts an account naming every one of them.': 'eine Bilanz annimmt, die jedes davon benennt.',
  'This run carries no close discipline, so it ends on its own prose.':
    'Dieser Lauf trägt keine Abschlussdisziplin, er endet also auf seiner eigenen Prosa.',
  '{proposals}, {questions} from {ref}': '{proposals}, {questions} aus {ref}',
  '{n} proposal': '{n} Vorschlag',
  '{n} proposals': '{n} Vorschläge',
  '{n} question': '{n} Frage',
  '{n} questions': '{n} Fragen',
  'nothing filed from {ref} yet': 'aus {ref} noch nichts eingereicht',
  'This pass walks the register rather than a document set, so there is no per-document tally — watch what it files.':
    'Dieser Durchgang durchläuft das Register statt eines Dokumentensatzes, es gibt also keine Zählung pro Dokument — beobachten Sie, was er einreicht.',
  'Filed so far — {proposals}, {questions}': 'Bisher eingereicht — {proposals}, {questions}',
  'no document cited': 'kein Dokument zitiert',
  'Nothing filed yet. Statements appear here the moment they reach the database.':
    'Noch nichts eingereicht. Aussagen erscheinen hier, sobald sie die Datenbank erreichen.',
  'Nudge — the agent reads this at its next turn boundary': 'Anstoss — der Agent liest dies an seiner nächsten Rundengrenze',
  'Nudge': 'Anstossen',

  // ── the verdict (one run = one sentence) ──
  'scope': 'Umfang',
  'document': 'Dokument',
  'documents': 'Dokumente',
  'read': 'gelesen',
  'walked the register': 'hat das Register durchlaufen',
  'in scope': 'im Umfang',
  'turns with you': 'Runden mit Ihnen',
  'no close discipline': 'keine Abschlussdisziplin',
  'claimed': 'behauptet',
  'filed,': 'eingereicht,',
  'on record': 'aktenkundig',
  'held': 'übernommen',
  'rejected': 'abgelehnt',
  'waiting on you': 'warten auf Sie',
  'filed': 'eingereicht',
  'recorded': 'erfasst',
  'fact': 'Fakt',
  'facts': 'Fakten',
  'event': 'Ereignis',
  'events': 'Ereignisse',

  // ── the closing account ──
  'The account it closed on': 'Die Bilanz, mit der er abgeschlossen hat',
  'No closing account.': 'Keine Abschlussbilanz.',
  'This pass carried a close discipline and never satisfied it, so there is no statement of what it did — only the transcript.':
    'Dieser Durchgang trug eine Abschlussdisziplin und hat sie nie erfüllt, es gibt also keine Aussage darüber, was er getan hat — nur das Transkript.',
  'A conversation and a plain targeted run end on their own prose; there is nothing to validate.':
    'Ein Gespräch und ein einfacher gezielter Lauf enden auf ihrer eigenen Prosa; es gibt nichts zu prüfen.',
  'Showing the whole account.': 'Die ganze Bilanz wird gezeigt.',
  'Showing the head of the account — {shown} of {total} lines.': 'Der Kopf der Bilanz wird gezeigt — {shown} von {total} Zeilen.',
  'Show the head': 'Nur den Kopf',
  'Full account': 'Ganze Bilanz',

  // ── the filed statements ──
  'pending review': 'Prüfung ausstehend',
  'rejected — {reason}': 'abgelehnt — {reason}',
  'no reason recorded': 'kein Grund erfasst',
  'accepted with edits': 'mit Änderungen übernommen',
  'superseded': 'ersetzt',
  'accepted‖proposal': 'übernommen',
  'What it filed': 'Was er eingereicht hat',
  'Every statement carries the span it was taken from.': 'Jede Aussage trägt die Quellenstelle, aus der sie stammt.',
  '(no text recorded)': '(kein Text erfasst)',
  'proposal #{id}': 'Vorschlag #{id}',
  'Definition of done': 'Abnahmekriterien',
  'document #{id}': 'Dokument #{id}',

  // ── the transcript ──
  'Transcript': 'Transkript',
  'No transcript was captured for this run.': 'Für diesen Lauf wurde kein Transkript erfasst.',
  'Transcript — the provenance record': 'Transkript — die Herkunftsaufzeichnung',
  'Every pushback the tool layer gave, in the words it gave them.': 'Jede Zurückweisung der Tool-Schicht, in den Worten, in denen sie sie gab.',
  'The pass prompt it was given — in full under Provenance, below.': 'Der Durchgangs-Prompt, den er erhielt — vollständig unter Herkunft, unten.',
  'You / the continuation': 'Sie / die Fortsetzung',
  'pushed back': 'zurückgewiesen',
  'missed': 'verfehlt',
  'ok': 'ok',

  // ── the run expansion / provenance ──
  'Could not read the run': 'Der Lauf konnte nicht gelesen werden',
  'This run could not be read': 'Dieser Lauf konnte nicht gelesen werden',
  'Reading the run…': 'Der Lauf wird gelesen…',
  'Provenance': 'Herkunft',
  'model': 'Modell',
  'skills': 'Skills',
  'config': 'Konfiguration',
  'tokens in': 'Tokens hinein',
  'out': 'heraus',
  'lookup misses': 'Fehlsuchen',
  'ordinary searching, not pushbacks': 'gewöhnliches Suchen, keine Zurückweisungen',
  'scope:': 'Umfang:',
  'Hide the prompt it was given': 'Den erhaltenen Prompt ausblenden',
  'The prompt it was given': 'Der erhaltene Prompt',
  'Open this thread': 'Diesen Thread öffnen',
  'Delete run': 'Lauf löschen',

  // ── acts / toasts ──
  'Run #{id} dispatched over {n} document': 'Lauf #{id} über {n} Dokument gestartet',
  'Run #{id} dispatched over {n} documents': 'Lauf #{id} über {n} Dokumente gestartet',
  '{label} started — proposals land in the review queue': '{label} gestartet — Vorschläge landen in der Warteschlange',
  'Could not dispatch the pass': 'Der Durchgang konnte nicht gestartet werden',
  'Run #{id} queued': 'Lauf #{id} eingereiht',
  'Could not start the run': 'Der Lauf konnte nicht gestartet werden',
  'Run #{id} stopped': 'Lauf #{id} gestoppt',
  'Could not stop the run': 'Der Lauf konnte nicht gestoppt werden',
  'Nudge queued — the agent reads it at its next turn boundary': 'Anstoss eingereiht — der Agent liest ihn an seiner nächsten Rundengrenze',
  'Nudge failed': 'Anstoss fehlgeschlagen',
  'Workspace committed {sha}': 'Workspace committet {sha}',
  'Workspace refresh failed': 'Workspace-Aktualisierung fehlgeschlagen',
  'Run #{id} deleted — {n} proposal kept': 'Lauf #{id} gelöscht — {n} Vorschlag behalten',
  'Run #{id} deleted — {n} proposals kept': 'Lauf #{id} gelöscht — {n} Vorschläge behalten',
  'Run #{id} deleted': 'Lauf #{id} gelöscht',
  'Could not delete the run': 'Der Lauf konnte nicht gelöscht werden',
  'Cleared {n} run': '{n} Lauf bereinigt',
  'Cleared {n} runs': '{n} Läufe bereinigt',
  '{cleared} — kept {n} that produced proposals': '{cleared} — {n} behalten, die Vorschläge erzeugt haben',
  'Nothing to clear — every finished run produced proposals': 'Nichts zu bereinigen — jeder beendete Lauf hat Vorschläge erzeugt',
  'Cleanup failed': 'Bereinigung fehlgeschlagen',
  'Could not open that thread': 'Dieser Thread konnte nicht geöffnet werden',
  '(no text in the reply — open the run below for its transcript)': '(kein Text in der Antwort — öffnen Sie den Lauf unten für sein Transkript)',
  'proposal #{id} · {kind}': 'Vorschlag #{id} · {kind}',
  'Failed': 'Fehlgeschlagen',
  '⏹ Stopped by you. The thread is kept — ask again to carry on.': '⏹ Von Ihnen gestoppt. Der Thread bleibt erhalten — fragen Sie erneut, um fortzufahren.',

  // ── masthead / no engagement ──
  'Agent': 'Agent',
  'no engagement selected': 'kein Auftrag ausgewählt',
  'Extraction is always scoped to one engagement': 'Extraktion ist immer auf einen Auftrag begrenzt',
  "A run reads that engagement's linked documents and files into its register. Pick an engagement in the top bar and its desk loads here.":
    'Ein Lauf liest die verknüpften Dokumente dieses Auftrags und reicht in sein Register ein. Wählen Sie oben einen Auftrag, und sein Desk lädt hier.',
  'Extraction against': 'Extraktion gegen',
  'in-scope requirements': 'Anforderungen im Umfang',
  'still draft': 'noch Entwurf',

  // ── zone 1 — the desk ──
  'In flight': 'Im Lauf',
  'Polled every 5 s. What you see below is what it has already': 'Alle 5 s abgefragt. Was Sie unten sehen, ist, was er bereits',
  ', not what it says.': ' hat, nicht was er sagt.',
  'Each card names the gap it closes and the refs it would touch. Everything lands in the review queue; no pass writes to the register.':
    'Jede Karte benennt die Lücke, die sie schliesst, und die Refs, die sie berühren würde. Alles landet in der Warteschlange; kein Durchgang schreibt ins Register.',
  'The dispatch deck': 'Das Start-Deck',
  'Reading the register for the gaps a pass would close…': 'Das Register wird nach Lücken gelesen, die ein Durchgang schliessen würde…',
  'Something else in scope?': 'Etwas anderes im Umfang?',
  'Choose documents…': 'Dokumente wählen…',

  // ── zone 2 — the ledger ──
  'Runs — the account each one gave of itself': 'Läufe — die Bilanz, die jeder von sich gab',
  'Newest first. Open a run for its closing account and its transcript.': 'Neueste zuerst. Öffnen Sie einen Lauf für seine Abschlussbilanz und sein Transkript.',
  'The standing fault.': 'Der stehende Fehler.',
  'of the': 'der',
  'tool pushbacks across every run on this engagement are one thing —': 'Tool-Zurückweisungen über alle Läufe dieses Auftrags sind ein und dasselbe —',
  'At review,': 'Bei der Prüfung waren',
  'rejections you made were': 'Ihrer Ablehnungen',
  'Both point at the same prompt work, not at a bad run.': 'Beides deutet auf dieselbe Prompt-Arbeit hin, nicht auf einen schlechten Lauf.',
  'The fault classes': 'Die Fehlerklassen',
  'The run ledger': 'Das Lauf-Journal',
  'Reading the ledger…': 'Das Journal wird gelesen…',
  'No run has been dispatched on this engagement': 'Auf diesem Auftrag wurde kein Lauf gestartet',
  'A batch pass would read the {n} linked document above and file what it finds into the review queue. Nothing it files reaches the register until you accept it.':
    'Ein Batch-Durchgang würde das {n} verknüpfte Dokument oben lesen und seine Funde in die Warteschlange einreichen. Nichts davon erreicht das Register, bis Sie es übernehmen.',
  'A batch pass would read the {n} linked documents above and file what it finds into the review queue. Nothing it files reaches the register until you accept it.':
    'Ein Batch-Durchgang würde die {n} verknüpften Dokumente oben lesen und seine Funde in die Warteschlange einreichen. Nichts davon erreicht das Register, bis Sie es übernehmen.',
  'Review {n} →': '{n} prüfen →',
  '{n} finished run produced nothing — no proposal, no fact, no claim of either.':
    '{n} beendeter Lauf hat nichts erzeugt — keinen Vorschlag, keinen Fakt, keine Behauptung von beidem.',
  '{n} finished runs produced nothing — no proposal, no fact, no claim of either.':
    '{n} beendete Läufe haben nichts erzeugt — keinen Vorschlag, keinen Fakt, keine Behauptung von beidem.',
  'Clear them': 'Bereinigen',

  // ── zone 3 — the corpus dock ──
  'Ask the corpus': 'Den Korpus fragen',
  'Anything the agent records here lands in the same review queue.': 'Alles, was der Agent hier erfasst, landet in derselben Warteschlange.',
  'Close the thread': 'Thread schliessen',
  'Open the thread': 'Thread öffnen',
  'Thread': 'Thread',
  'is open.': 'ist offen.',
  'Last thread': 'Letzter Thread',
  '{proposals}, {held} held. Idle since {since}.': '{proposals}, {held} übernommen. Inaktiv seit {since}.',
  'No thread yet. Ask about the corpus — “which documents mention OCR?”': 'Noch kein Thread. Fragen Sie den Korpus — «welche Dokumente erwähnen OCR?»',
  'Ask about the corpus — “what did the sponsor say about the data room?”, “which documents mention OCR?”. Anything the agent proposes lands in the review queue, never in the register.':
    'Fragen Sie den Korpus — «was hat der Sponsor zum Datenraum gesagt?», «welche Dokumente erwähnen OCR?». Alles, was der Agent vorschlägt, landet in der Warteschlange, nie im Register.',
  'You': 'Sie',
  'thinking…': 'denkt nach…',
  'Ask the corpus…': 'Den Korpus fragen…',
  'vLLM is unreachable — the turn would die immediately.': 'vLLM ist nicht erreichbar — die Runde würde sofort sterben.',
  'Send': 'Senden',
  'Start a new thread — the current one stays in the ledger': 'Neuen Thread beginnen — der aktuelle bleibt im Journal',
  'New thread': 'Neuer Thread',

  // ── the delete modal ──
  'Delete run #{id}?': 'Lauf #{id} löschen?',
  'Delete': 'Löschen',
  'This removes the run and its transcript for good.': 'Das entfernt den Lauf und sein Transkript endgültig.',
  'Its': 'Seine',
  'proposal(s) are kept — they keep their statement and evidence, but lose the link back to this transcript, so “why did it think that?” can no longer be answered by reading the run.':
    'Vorschläge bleiben erhalten — sie behalten Aussage und Beleg, verlieren aber den Rückverweis auf dieses Transkript, sodass «warum dachte er das?» nicht mehr durch Lesen des Laufs beantwortet werden kann.',
  'It produced no proposals, so nothing else is affected.': 'Er hat keine Vorschläge erzeugt, sonst ist also nichts betroffen.',
  'The deletion itself is recorded in the audit log.': 'Die Löschung selbst wird im Prüfpfad festgehalten.',

  // ── the two faces (Desk | Proposals — the queue folded in as a sub-tab) ──
  'Desk': 'Desk',
  'Proposals': 'Vorschläge',
}, 'AgentPage');
